import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import clsx from "clsx";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "danger"
  | "ghost"
  | "icon"
  | "unstyled";

type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  iconPosition?: "left" | "right";
  fullWidth?: boolean;
  loading?: boolean;
  selected?: boolean;
  children?: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "border-none bg-primary text-white hover:opacity-85 disabled:bg-border disabled:text-tertiary disabled:opacity-100 disabled:cursor-not-allowed",
  secondary:
    "border border-border bg-white text-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed",
  danger:
    "border border-error bg-transparent text-error hover:bg-error hover:text-white disabled:opacity-50 disabled:cursor-not-allowed",
  ghost:
    "border-none bg-transparent text-tertiary hover:bg-subtle hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed",
  icon: "border-none bg-transparent text-tertiary hover:bg-subtle hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed",
  unstyled: "",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg",
  md: "gap-2 px-4 py-2 text-sm font-bold rounded-lg",
  lg: "gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl",
};

const iconSizeClasses: Record<ButtonSize, string> = {
  sm: "size-6 rounded-md p-0",
  md: "size-8 rounded-lg p-0",
  lg: "size-10 rounded-xl p-0",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "secondary",
      size = "md",
      icon,
      iconPosition = "left",
      fullWidth = false,
      loading = false,
      selected,
      disabled,
      className,
      children,
      type = "button",
      ...props
    },
    ref,
  ) => {
    const isUnstyled = variant === "unstyled";
    const isIconOnly = !children && !!icon;
    const iconClassName =
      isIconOnly && size === "sm"
        ? "text-base"
        : isIconOnly && size === "lg"
          ? "text-xl"
          : isIconOnly
            ? "text-lg"
            : "text-base";
    const iconMarkup = loading ? (
      <span className="loading-spinner size-4 border-2 border-current/30 border-t-current" />
    ) : icon ? (
      <span
        className={clsx("material-symbols-outlined", iconClassName)}
        aria-hidden
      >
        {icon}
      </span>
    ) : null;

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={clsx(
          !isUnstyled &&
            "inline-flex cursor-pointer items-center justify-center font-sans transition-colors",
          !isUnstyled && variantClasses[variant],
          !isUnstyled &&
            (isIconOnly ? iconSizeClasses[size] : sizeClasses[size]),
          !isUnstyled && fullWidth && "w-full",
          !isUnstyled && selected && "bg-subtle text-primary",
          className,
        )}
        {...props}
        aria-pressed={
          props["aria-pressed"] ??
          (selected === undefined ? undefined : selected)
        }
      >
        {iconPosition === "left" && iconMarkup}
        {children}
        {iconPosition === "right" && iconMarkup}
      </button>
    );
  },
);

Button.displayName = "Button";
